import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * A panel is a bordered surface, never a floating card: shadow is reserved for
 * true overlays. Padding is a prop rather than a constant so a dense table can
 * sit flush to the border while prose gets room.
 */
export interface PanelProps {
  children: ReactNode;
  className?: string;
  /** `none` lets tables and lists run edge to edge inside the border. */
  padding?: 'none' | 'tight' | 'normal' | 'loose';
  as?: 'div' | 'section' | 'article' | 'aside';
}

const PADDING = {
  none: '',
  tight: 'p-3',
  normal: 'p-4',
  loose: 'p-6',
} as const;

export function Panel({ children, className, padding = 'normal', as: Tag = 'div' }: PanelProps) {
  return (
    <Tag className={cn('rounded-md border border-line bg-surface', PADDING[padding], className)}>
      {children}
    </Tag>
  );
}

export interface PanelHeaderProps {
  title: ReactNode;
  /** Small uppercase label above the title. */
  eyebrow?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

export function PanelHeader({
  title,
  eyebrow,
  description,
  actions,
  className,
}: PanelHeaderProps) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3',
        className,
      )}
    >
      <div className="min-w-0">
        {eyebrow ? <p className="eyebrow mb-1">{eyebrow}</p> : null}
        <h2 className="text-md font-semibold leading-snug tracking-tight text-fg">{title}</h2>
        {description ? (
          <p className="mt-1 max-w-prose text-sm text-fg-secondary">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/**
 * Page-level section heading. More space above than below, so a section reads
 * as attached to what follows it rather than floating between two blocks.
 */
export function SectionHeading({
  children,
  actions,
  className,
}: {
  children: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    // Wraps rather than overflows: the Traffic total («2 платежа · 250 000,00 ₽
    // · 2 500,00 USDT») is wider than a 320px viewport on its own, and an
    // unwrapped flex row would push the whole page sideways.
    <div
      className={cn(
        'mt-12 mb-3 flex flex-wrap items-end justify-between gap-x-4 gap-y-1.5',
        className,
      )}
    >
      <h2 className="eyebrow">{children}</h2>
      {actions ? <div className="flex min-w-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}
